<template>
  <div class="welcome-page">
    <header class="site-header">
      <router-link to="/welcome" class="brand-link" aria-label="MyStudio">
        <img src="/Pagina%20Welcome/logo.png" alt="" class="brand-logo" />
        <span>MyStudio</span>
      </router-link>

      <nav class="main-nav" aria-label="Navigazione principale">
        <button @click="scrollTo('about')">Chi siamo</button>
        <button @click="scrollTo('product')">Piattaforma MyStudio</button>
        <button @click="scrollTo('tools')">Strumenti</button>
        <button @click="scrollTo('contacts')">Contatti</button>
      </nav>

      <router-link to="/users-login" class="client-area-link">
        <span class="button-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="8" r="3.2" />
            <path d="M6.5 19a5.5 5.5 0 0 1 11 0" />
            <path d="M17.5 12.2v-1.4a2.2 2.2 0 0 1 4.4 0v1.4" />
            <rect x="16.6" y="12.2" width="6.2" height="5.2" rx="1.2" />
          </svg>
        </span>
        <span>Area clienti</span>
      </router-link>
    </header>

    <main>
      <section class="hero-section">
        <div class="hero-visual" aria-hidden="true">
          <picture>
            <source
              media="(min-width: 1081px) and (max-height: 920px)"
              srcset="/Pagina%20Welcome/sfondo2.png"
            />
            <img src="/Pagina%20Welcome/sfondo2-wide-2048x941.png" alt="" />
          </picture>
        </div>
        <div class="hero-inner">
          <div class="hero-content">
            <h1>
              <span class="hero-title-line">Tecnologia</span>
              <span class="hero-title-line">pensata per il</span>
              <span class="hero-title-line">lavoro degli</span>
              <span class="hero-title-line">studi contabili.</span>
            </h1>
            <p class="hero-copy">
              Semplifichiamo e automatizziamo i processi contabili attraverso
              soluzioni digitali intelligenti mettendo in comunicazione software
              contabili differenti. Da questa visione nasce la
              <strong>Piattaforma MyStudio.</strong>
            </p>
            <div class="hero-actions">
              <router-link to="/users-login" class="primary-action">
                Scopri la Piattaforma MyStudio
              </router-link>
              <button class="secondary-action" @click="scrollTo('about')">
                Chi siamo
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="about" class="section about-section">
        <div class="section-heading">
          <p class="eyebrow">Chi &egrave; MyStudio</p>
          <h2>Innovazione che nasce da esigenze reali.</h2>
          <p>
            MyStudio &egrave; una start-up innovativa che sviluppa soluzioni
            tecnologiche dedicate agli studi contabili. Un team
            multidisciplinare unisce competenze contabili, sviluppo software,
            algoritmi e analisi dei dati per trasformare le attivit&agrave; in
            processi semplici, veloci e controllabili.
          </p>
        </div>

        <div class="pillars-grid" aria-label="Pilastri MyStudio">
          <article v-for="pillar in pillars" :key="pillar.title" class="pillar">
            <img :src="pillar.icon" alt="" class="icon-badge" />
            <h3>{{ pillar.title }}</h3>
            <p>{{ pillar.text }}</p>
          </article>
        </div>
      </section>

      <section id="vision" class="vision-band">
        <img src="/icons/visione.png" alt="" class="vision-icon" />
        <div class="vision-title">
          <p class="eyebrow vision-eyebrow">La nostra visione</p>
          <h2>La tecnologia deve semplificare il lavoro, non complicarlo.</h2>
        </div>
        <p class="vision-copy">
          Crediamo che la digitalizzazione non significhi aggiungere nuovi
          strumenti, ma eliminare passaggi inutili. Per questo sviluppiamo
          soluzioni pensate per integrarsi con il modo in cui gli studi lavorano
          gi&agrave; oggi. Il nostro focus &egrave; facilitare l'inserimento
          dati nel tuo gestionale senza alterare il modo di lavorare.
        </p>
      </section>

      <section id="product" class="section product-section">
        <div class="product-copy">
          <p class="eyebrow">Il nostro prodotto</p>
          <h2>MyStudio App</h2>
          <p class="product-subtitle">
            Il tuo studio contabile, pi&ugrave; intelligente.
          </p>
          <p>
            La web app che automatizza l'elaborazione dei documenti contabili,
            struttura i dati e genera output pronti per il tuo gestionale.
          </p>
          <router-link to="/users-login" class="primary-action">
            Scopri la piattaforma MyStudio
          </router-link>
        </div>

        <ul
          class="product-checklist"
          aria-label="Funzionalit&agrave; principali"
        >
          <li>Elaborazione rapida e automatizzata</li>
          <li>Piani intelligenti che imparano dal tuo studio</li>
          <li>Output pronti per l'importazione diretta</li>
          <li>Compatibile con i principali software contabili</li>
        </ul>

        <div class="product-visual">
          <img
            src="/Pagina%20Welcome/homepage.png"
            alt="Schermata home di MyStudio"
          />
        </div>
      </section>

      <section id="advantages" class="section advantages-section">
        <h2>Cosa rende la Piattaforma MyStudio diversa?</h2>
        <div class="advantages-grid">
          <article
            v-for="advantage in advantages"
            :key="advantage.title"
            class="advantage"
          >
            <img :src="advantage.icon" alt="" class="line-icon" />
            <h3>{{ advantage.title }}</h3>
            <p>{{ advantage.text }}</p>
          </article>
        </div>
      </section>

      <section id="tools" class="section tools-section">
        <p class="eyebrow tools-eyebrow">I nostri strumenti</p>
        <h2 class="tools-title">Convertitori</h2>
        <div class="tools-grid">
          <article
            v-for="tool in tools"
            :key="tool.title"
            class="tool-card"
            :class="`tool-card-${tool.color}`"
          >
            <div class="tool-card-title">
              <span
                class="tool-icon"
                :class="`tool-icon-${tool.color}`"
                aria-hidden="true"
              >
                {{ tool.icon }}
              </span>
              <h3>{{ tool.title }}</h3>
            </div>
            <p>{{ tool.text }}</p>
          </article>
        </div>

        <p class="coming-soon">
          <img src="/icons/razzo.png" alt="" class="rocket-icon" />
          Altri strumenti sono in sviluppo.
        </p>
      </section>

      <section
        id="resources"
        class="resources-image-section"
        aria-label="Intelligenza artificiale e compatibilità software MyStudio"
      >
        <img
          class="resources-composite-image"
          src="/Pagina%20Welcome/Intelligenza%20artificiale%20per%20documenti.png"
          alt="MyStudio integra l'intelligenza artificiale per elaborare 100 pagine PDF in circa 4 secondi e collega software contabili diversi, con compatibilità TeamSystem, Datev, Zucchetti, SEAC e Sistemi."
        />
      </section>

      <section id="team" class="section team-section">
        <h2>Le persone dietro MyStudio</h2>
        <div class="team-grid">
          <article
            v-for="(member, index) in team"
            :key="member.name"
            class="team-card"
            :class="{ 'team-card-open': isTeamOpen(index) }"
            role="button"
            tabindex="0"
            :aria-expanded="isTeamOpen(index) ? 'true' : 'false'"
            @click="toggleTeam(index)"
            @keydown.enter.prevent="toggleTeam(index)"
            @keydown.space.prevent="toggleTeam(index)"
          >
            <div class="team-summary">
              <img :src="member.photo" :alt="member.name" />
              <span class="team-summary-text">
                <h3>{{ member.name }}</h3>
                <p>{{ member.role }}</p>
                <span class="team-more">{{ teamToggleLabel(index) }}</span>
              </span>
            </div>

            <transition name="team-detail">
              <div v-if="isTeamOpen(index)" class="team-detail">
                <p>{{ member.bio }}</p>
                <blockquote>{{ member.quote }}</blockquote>
              </div>
            </transition>
          </article>
        </div>
      </section>

      <section class="final-cta">
        <div class="cta-copy">
          <span class="cta-icon-shell">
            <img src="/icons/razzo.png" alt="" class="cta-icon" />
          </span>
          <div>
            <h2>Vuoi vedere MyStudio al lavoro nel tuo studio?</h2>
            <p>
              Registrati gratuitamente e prova i nostri strumenti con i tuoi
              documenti.
            </p>
          </div>
        </div>
        <div class="cta-actions">
          <router-link to="/users-login" class="primary-action">
            Prova MyStudio gratuitamente
          </router-link>
        </div>
      </section>
    </main>

    <footer id="contacts" class="site-footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="footer-brand-row">
            <img src="/Pagina%20Welcome/logo.png" alt="" />
            <strong>MyStudio</strong>
          </div>
          <p>&copy; 2025 MyStudio. Tutti i diritti riservati.</p>
        </div>
        <div class="footer-links">
          <span>Link utili</span>
          <router-link to="/users-login">Piattaforma MyStudio</router-link>
          <a
            href="/Pagina%20Welcome/MyStudio%20-%20Informativa%20Privacy.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          <a
            href="/Pagina%20Welcome/MyStudio%20-%20Termini%20e%20condizioni%20di%20utilizzo.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Termini e condizioni
          </a>
        </div>
        <div class="footer-contact">
          <span>Contatti</span>
          <a href="mailto:info@mystudioapp.it">info@mystudioapp.it</a>
        </div>
        <div class="footer-social">
          <span>Seguici</span>
          <div class="social-icons" aria-label="Canali social">
            <a
              href="https://www.linkedin.com/company/mystudio-app/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn MyStudio"
              title="Pagina Linkedin"
            >
              in
            </a>
            <a
              href="https://www.instagram.com/mystudio.srl/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram MyStudio"
              title="Pagina Instagram"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M7.5 2.8h9A4.7 4.7 0 0 1 21.2 7.5v9a4.7 4.7 0 0 1-4.7 4.7h-9a4.7 4.7 0 0 1-4.7-4.7v-9a4.7 4.7 0 0 1 4.7-4.7Z"
                />
                <circle cx="12" cy="12" r="3.7" />
                <circle cx="17.1" cy="6.9" r="1" />
              </svg>
            </a>
          </div>
        </div>
        <p class="footer-company">
          MYSTUDIO SRL - P.IVA 01772100531 - Via del Sugheraio n. 230, Follonica
          (GR)
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from "axios";
import {
  getClerk,
  getClerkProfile,
  getClerkToken,
  isClerkConfigured,
} from "@/auth/clerk.js";
import { API_BASE_URL } from "@/config";
import { disableGuestMode } from "@/utils/guestMode";

const icon = (content) =>
  `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${content}</svg>`;

const svg = {
  clipboard: icon(
    '<path d="M9 4h6l1 3H8l1-3Z"/><rect x="6" y="6" width="12" height="15" rx="2"/><path d="M9 12h6M9 16h6"/>'
  ),
  file: icon('<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v5h5M9 13h6M9 17h6"/>'),
  chart: icon(
    '<path d="M4 19h16"/><rect x="6" y="10" width="3" height="6"/><rect x="11" y="6" width="3" height="10"/><rect x="16" y="12" width="3" height="4"/>'
  ),
  document: icon(
    '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v5h5M9 12h6M9 16h6"/>'
  ),
  aiBrain: icon(
    '<path d="M10 4.4C7.3 4.4 5.2 6.5 5.2 9.1c0 .4 0 .7.1 1A4.7 4.7 0 0 0 7.8 19h2.8"/><path d="M10 4.4c2.7 0 4.8 2.1 4.8 4.7v9.5"/><path d="M9.8 8.2c.8.3 1.4.9 1.7 1.7M7.3 11.5c.7.5 1.5.8 2.4.8M9 15.4c1.1-.1 2-.6 2.6-1.5"/><path d="M14.8 8.2h2.4l1.3-1.3M14.8 11.7h3.6M14.8 15.2h2.4l1.3 1.3"/><circle cx="20" cy="5.4" r="1.2"/><circle cx="20.2" cy="11.7" r="1.2"/><circle cx="20" cy="18" r="1.2"/>'
  ),
};

export default {
  name: "WelcomePage",
  data() {
    return {
      activeTeams: [],
      icons: svg,
      pillars: [
        {
          icon: "/icons/competenza.png",
          title: "Competenza contabile",
          text: "Partiamo dai processi reali dello studio.",
        },
        {
          icon: "/icons/tecnologia.png",
          title: "Tecnologia",
          text: "Trasformiamo quei processi in soluzioni software.",
        },
        {
          icon: "/icons/innovazione.png",
          title: "Innovazione",
          text: "Automazione, algoritmi e Intelligenza Artificiale al servizio degli studi.",
        },
      ],
      advantages: [
        {
          icon: "/icons/automazione.png",
          title: "Automazione",
          text: "Riduce le operazioni manuali e le attività ripetitive legate all'elaborazione dei documenti.",
        },
        {
          icon: "/icons/piani-intelligenti.png",
          title: "Piani intelligenti",
          text: "Le configurazioni e le associazioni vengono salvate e riutilizzate nelle elaborazioni future.",
        },
        {
          icon: "/icons/trasversalita.png",
          title: "Trasversalità",
          text: "Elabora documenti provenienti da software diversi e genera output per il gestionale che preferisci.",
        },
        {
          icon: "/icons/intelligenza-artificiale.png",
          title: "Intelligenza Artificiale",
          text: "Nuovi algoritmi AI rendono l'elaborazione sempre più rapida, efficiente e affidabile.",
        },
      ],
      tools: [
        {
          icon: "📋",
          color: "green",
          title: "Prospetti Paghe",
          text: "Elabora e importa i tuoi Prospetti Paghe in pochi click, già pronti per l'integrazione nel tuo gestionale.",
          route: "/users-login",
        },
        {
          icon: "📑",
          color: "blue",
          title: "Modelli F24",
          text: "Converti i tuoi Modelli F24 in formati standard e velocizza la registrazione contabile nel tuo gestionale.",
          route: "/users-login",
        },
        {
          icon: "📄",
          color: "violet",
          title: "Bilanci Analitici",
          text: "Gestisci i conti dei bilanci secondo il gestionale che preferisci, mantenendo coerenza e precisione nei dati.",
          route: "/users-login",
        },
      ],
      team: [
        {
          name: "Francesco Stella",
          photo: "/Pagina%20Welcome/francesco.jpg",
          role: "Full Stack Developer",
          bio: "Laureato in Informatica Umanistica all'Università di Pisa, coniuga creatività e precisione tecnica nello sviluppo di interfacce e soluzioni digitali in ambienti Cloud. Ha progettato e implementato l'architettura della piattaforma, curando l'integrazione tra frontend e backend e la realizzazione dell'interfaccia grafica, dando forma al volto di MyStudio.",
          quote: "Ogni interfaccia è un dialogo tra persone e codice.",
        },
        {
          name: "Filippo Frassinetti",
          photo: "/Pagina%20Welcome/filippo.jpg",
          role: "Software Engineer",
          bio: "Laureato in Ingegneria Robotica e Automazione all'Università di Pisa, combina rigore analitico e creatività nello sviluppo di algoritmi per sistemi autonomi e soluzioni software avanzate. Ha sviluppato gli algoritmi intelligenti che elaborano i dati contabili con efficienza e precisione, dando forma al cuore tecnologico della piattaforma MyStudio.",
          quote:
            "La parte più affascinante è vedere un sistema prendere vita da un'idea matematica.",
        },
        {
          name: "Giorgio Nobili",
          photo: "/Pagina%20Welcome/giorgio.png",
          role: "Commercialista e Revisore Legale",
          bio: "Con oltre venticinque anni di esperienza nella consulenza contabile e fiscale, unisce competenza tecnica e visione innovativa nella gestione dei processi interni degli studi commerciali. Da questa esperienza nasce l'idea alla base di MyStudio: automatizzare l'elaborazione dei documenti contabili per rendere la gestione dei dati più sicura, efficiente e precisa.",
          quote:
            "Credo che la tecnologia debba semplificare il lavoro, non complicarlo.",
        },
      ],
    };
  },
  mounted() {
    this.completeInterruptedClerkLogin();
  },
  methods: {
    cacheAuthSession(response, emailFallback = "") {
      const email = response.data.user_email || emailFallback;
      const userData = {
        email,
        first_name: response.data.first_name || "",
        last_name: response.data.last_name || "",
        is_admin: response.data.is_admin || false,
        is_admin_MyStudio: response.data.is_admin_MyStudio || false,
        studio: {
          nome: response.data.studio_nome || "Studio",
          piano: response.data.piano || "base",
        },
      };

      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("userEmail", email);
      localStorage.setItem(
        "isAdminMyStudio",
        JSON.stringify(response.data.is_admin_MyStudio)
      );
      localStorage.setItem("isAdmin", JSON.stringify(response.data.is_admin));
      localStorage.setItem("firstName", response.data.first_name || "");
      localStorage.setItem("lastName", response.data.last_name || "");
    },
    async completeInterruptedClerkLogin() {
      if (
        !isClerkConfigured ||
        sessionStorage.getItem("mystudioClerkBridgePending") !== "1"
      ) {
        return;
      }

      try {
        const clerk = await getClerk();
        if (!clerk.isSignedIn) {
          return;
        }

        const token = await getClerkToken();
        if (!token) {
          return;
        }

        disableGuestMode();
        const profile = getClerkProfile(clerk);
        const response = await axios.post(
          `${API_BASE_URL}/clerk/session`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            skipGlobalErrorLog: true,
          }
        );

        this.cacheAuthSession(response, profile.email);
        sessionStorage.removeItem("mystudioClerkBridgePending");
        await this.$router.replace("/home");
      } catch (error) {
        console.warn("Recupero sessione Clerk interrotta non riuscito", error);
      }
    },
    scrollTo(id) {
      const section = document.getElementById(id);
      if (section) {
        const header = document.querySelector(".site-header");
        const headerStyle = header ? window.getComputedStyle(header) : null;
        const headerHeight =
          header && headerStyle?.position === "sticky"
            ? header.offsetHeight
            : 0;
        const extraOffset = id === "resources" ? 42 : 22;
        const targetTop =
          section.getBoundingClientRect().top +
          window.pageYOffset -
          headerHeight -
          extraOffset;

        window.scrollTo({ top: targetTop, behavior: "smooth" });
      }
    },
    isTeamOpen(index) {
      return this.activeTeams.includes(index);
    },
    teamToggleLabel(index) {
      return this.isTeamOpen(index) ? "Nascondi profilo" : "Leggi profilo";
    },
    toggleTeam(index) {
      const currentIndex = this.activeTeams.indexOf(index);
      if (currentIndex === -1) {
        this.activeTeams.push(index);
        return;
      }

      this.activeTeams.splice(currentIndex, 1);
    },
  },
};
</script>

<style scoped>
.welcome-page {
  --ink: #123f36;
  --muted: #54665f;
  --green: #0d6b4c;
  --green-dark: #064a36;
  --section-green: #007a3f;
  --green-soft: #e8f3ed;
  --mint: #f4faf7;
  --border: #d7e4dd;
  --shadow: 0 16px 36px rgba(19, 63, 54, 0.12);
  min-height: 100vh;
  overflow-x: hidden;
  background: #fbfdfb;
  color: var(--ink);
  font-family: Avenir, Helvetica, Arial, sans-serif;
  line-height: 1.55;
}

.welcome-page section,
.site-footer {
  scroll-margin-top: 96px;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1.25rem;
  min-height: 66px;
  padding: 0.5rem clamp(1.25rem, 4vw, 3.6rem);
  border-bottom: 1px solid rgba(18, 63, 54, 0.12);
  background: rgba(246, 251, 248, 0.94);
  backdrop-filter: blur(12px);
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.brand-link,
.client-area-link,
.primary-action,
.secondary-action {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, color 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease;
}

.brand-link {
  gap: 0.62rem;
  color: var(--ink);
  font-size: 1.28rem;
  font-weight: 700;
}

.brand-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.main-nav {
  display: flex;
  justify-content: center;
  gap: 0;
}

.main-nav button {
  position: relative;
  border: 0;
  background: transparent;
  color: #102f29;
  cursor: pointer;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 0.98rem;
  font-weight: 800;
  padding: 0.3rem clamp(1.15rem, 1.7vw, 1.75rem);
  white-space: nowrap;
}

.main-nav button + button::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 1px;
  height: 1rem;
  background: rgba(18, 63, 54, 0.2);
  transform: translateY(-50%);
}

.main-nav button:hover {
  color: var(--green);
}

.client-area-link {
  gap: 0.45rem;
  min-height: 44px;
  padding: 0.27rem 1.02rem;
  border: 1px solid var(--green);
  background: #fff;
  color: var(--green-dark);
  font-size: 0.84rem;
  text-transform: uppercase;
  box-shadow: 0 6px 16px rgba(18, 63, 54, 0.06);
}

.button-icon,
.tool-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.flow-node svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.button-icon {
  width: 20px;
  height: 20px;
  color: currentColor;
}

.button-icon svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.85;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.client-area-link:hover {
  background: var(--green);
  border-color: var(--green);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(13, 107, 76, 0.22);
}

.primary-action:hover {
  background: var(--green-dark);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(13, 107, 76, 0.2);
}

.hero-section {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 38%) minmax(0, 62%);
  width: 100%;
  margin: 0 auto;
  align-items: center;
  overflow: hidden;
  background: #fbfdfb;
}

.hero-visual {
  grid-column: 2;
  grid-row: 1;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.hero-visual picture,
.hero-visual img {
  display: block;
  width: 100%;
  height: auto;
}

.hero-visual img {
  object-fit: contain;
}

.hero-inner {
  position: relative;
  z-index: 1;
  grid-column: 1;
  grid-row: 1;
  width: auto;
  margin: 0;
  padding: 2.2rem 0 2.3rem clamp(2rem, 8vw, 14rem);
  box-sizing: border-box;
}

.hero-content {
  position: relative;
  width: min(100%, 455px);
  padding: 0;
  text-align: left;
}

.eyebrow {
  margin: 0 0 0.75rem;
  color: var(--section-green);
  font-size: 0.85rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
  text-align: left;
}

.hero-content h1 {
  max-width: 440px;
  margin: 0;
  font-size: clamp(2.55rem, 4vw, 4.05rem);
  line-height: 0.98;
  letter-spacing: 0;
}

.hero-title-line {
  display: block;
  white-space: nowrap;
}

.hero-content h1::after,
.section-heading h2::after,
.vision-title h2::after {
  content: "";
  display: block;
  width: 46px;
  height: 2px;
  margin-top: 1rem;
  border-radius: 999px;
  background: var(--section-green);
}

.hero-copy {
  max-width: 395px;
  margin: 1.2rem 0 0;
  color: #263e37;
  font-size: 0.9rem;
  line-height: 1.62;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin-top: 1.7rem;
}

.primary-action,
.secondary-action {
  min-height: 33px;
  border: 1px solid var(--green);
  cursor: pointer;
  font-family: inherit;
  padding: 0.68rem 1.18rem;
  font-size: 0.7rem;
  line-height: 1.25;
  text-align: center;
  text-transform: uppercase;
  white-space: normal;
}

.primary-action {
  background: var(--green);
  color: #fff;
  box-shadow: 0 12px 24px rgba(13, 107, 76, 0.18);
}

.secondary-action {
  background: rgba(255, 255, 255, 0.75);
  color: var(--ink);
}

.secondary-action:hover {
  background: #fff;
  border-color: var(--green-dark);
  color: var(--green-dark);
  transform: translateY(-1px);
}

.section {
  width: min(100% - 2rem, 1180px);
  margin: 0 auto;
  padding: clamp(3.6rem, 6.5vw, 5.8rem) 0;
}

.about-section {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(430px, 1fr);
  gap: clamp(2.7rem, 6vw, 5.6rem);
  align-items: center;
  padding-bottom: clamp(1.6rem, 3.2vw, 2.6rem);
}

.section-heading {
  text-align: left;
}

.section-heading h2,
.vision-title h2,
.product-copy h2,
.advantages-section h2,
.tools-section h2,
.team-section h2,
.final-cta h2 {
  margin: 0;
  color: var(--ink);
  font-size: clamp(1.85rem, 3vw, 2.95rem);
  line-height: 1.06;
  letter-spacing: 0;
}

.section-heading p:not(.eyebrow),
.vision-copy,
.resource-panel p,
.final-cta p {
  color: var(--muted);
  font-size: 0.95rem;
}

.section-heading p:not(.eyebrow) {
  max-width: 455px;
  margin-top: 1.35rem;
  margin-bottom: 0;
}

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;
}

.tool-card,
.team-card,
.resource-panel {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(19, 63, 54, 0.08);
}

.pillar {
  min-height: 188px;
  padding: 1.1rem 1.2rem;
  background: transparent;
  border-left: 1px solid var(--border);
  text-align: center;
}

.pillar:first-child {
  border-left: 0;
}

.icon-badge {
  display: block;
  width: 82px;
  height: 82px;
  margin: 0 auto 1rem;
  object-fit: contain;
}

.pillar h3,
.advantage h3,
.tool-card h3,
.team-card h3 {
  margin: 0 0 0.45rem;
  color: var(--ink);
  font-size: 0.95rem;
  line-height: 1.2;
}

.pillar p,
.advantage p,
.tool-card p,
.team-card p {
  margin: 0;
  color: var(--muted);
  font-size: 0.86rem;
  line-height: 1.55;
}

.vision-band {
  display: grid;
  grid-template-columns: 250px minmax(330px, 0.9fr) minmax(360px, 1fr);
  align-items: center;
  column-gap: clamp(2.2rem, 4.2vw, 4.2rem);
  row-gap: 1rem;
  padding: clamp(2.7rem, 4.8vw, 4.2rem) max(1rem, calc((100vw - 1180px) / 2));
  background: linear-gradient(90deg, #eaf4ee, #f7fbf8);
}

.vision-eyebrow {
  margin-bottom: 1rem;
  color: var(--section-green);
}

.vision-icon {
  display: block;
  width: clamp(210px, 17vw, 265px);
  height: clamp(210px, 17vw, 265px);
  object-fit: contain;
}

.vision-title {
  text-align: left;
}

.vision-title h2 {
  max-width: 430px;
  font-size: clamp(2rem, 2.65vw, 3rem);
  line-height: 1.08;
  text-align: left;
}

.vision-title h2::after {
  margin-left: 0;
}

.vision-copy {
  margin: 0;
  max-width: 440px;
  font-size: 0.98rem;
  line-height: 1.72;
  text-align: left;
}

.product-section {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(220px, 0.62fr) minmax(
      465px,
      1.22fr
    );
  align-items: center;
  width: min(100% - 2rem, 1240px);
  gap: clamp(1.6rem, 3vw, 2.8rem);
  padding-top: clamp(4.2rem, 6.7vw, 6.2rem);
  padding-bottom: clamp(1.5rem, 3vw, 2.5rem);
}

.product-copy {
  text-align: left;
}

.product-copy .eyebrow {
  margin-bottom: 0.45rem;
}

.product-copy h2 {
  font-size: clamp(1.95rem, 3vw, 2.65rem);
}

.product-subtitle {
  margin: 0.2rem 0 0;
  color: var(--ink);
  font-size: clamp(1.05rem, 1.5vw, 1.25rem);
  font-weight: 700;
}

.product-copy p:not(.eyebrow):not(.product-subtitle) {
  max-width: 390px;
  margin: 1rem 0 0;
  color: var(--muted);
  font-size: 0.95rem;
  line-height: 1.68;
}

.product-copy .primary-action {
  margin-top: 1.35rem;
}

.product-checklist {
  display: grid;
  gap: 0.7rem;
  margin: 0;
  padding: 0;
  color: var(--ink);
  list-style: none;
  text-align: left;
}

.product-checklist li {
  position: relative;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: start;
  gap: 0.58rem;
  font-size: 0.92rem;
  line-height: 1.45;
}

.product-checklist li::before {
  content: "";
  width: 18px;
  height: 18px;
  margin-top: 0.1rem;
  border: 2px solid var(--section-green);
  border-radius: 50%;
  background: #fff;
}

.product-checklist li::after {
  content: "";
  position: absolute;
  top: 0.46rem;
  left: 0.32rem;
  width: 8px;
  height: 4px;
  border-bottom: 2px solid var(--section-green);
  border-left: 2px solid var(--section-green);
  transform: rotate(-45deg);
  transform-origin: center;
}

.product-visual {
  min-width: 0;
}

.product-visual img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(18, 63, 54, 0.08);
}

.advantages-section,
.tools-section,
.team-section {
  text-align: center;
}

.advantages-section {
  padding-top: clamp(1.6rem, 3vw, 2.7rem);
}

.advantages-section h2 {
  font-size: clamp(1.7rem, 2.45vw, 2.5rem);
}

.advantages-section > .eyebrow {
  margin-bottom: 1.15rem;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin-top: 2.25rem;
}

.advantage {
  min-height: 210px;
  padding: 1.2rem 1.35rem;
  border-left: 1px solid var(--border);
  background: transparent;
  box-shadow: none;
}

.advantage:first-child {
  border-left: 0;
}

.line-icon {
  display: block;
  width: 82px;
  height: 82px;
  margin: 0 auto 0.9rem;
  object-fit: contain;
}

.tools-section {
  width: 100%;
  max-width: none;
  background: linear-gradient(90deg, #eaf4ee, #f7fbf8);
  padding-top: 3.2rem;
  padding-bottom: 2.2rem;
}

.tools-section > * {
  width: min(100% - 2rem, 1180px);
  margin-right: auto;
  margin-left: auto;
}

.tools-eyebrow {
  display: block;
  margin-bottom: 0.35rem;
  text-align: left;
}

.tools-section .tools-title {
  display: inline-block;
  position: relative;
  margin: 0;
  color: #2e7d32;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.tools-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  width: min(100% - 2rem, 1080px);
  margin: 1.55rem auto 0;
  padding: 0 0.5rem 1.4rem;
  text-align: center;
}

.tool-card {
  box-sizing: border-box;
  flex: 0 1 310px;
  min-width: 0;
  max-width: 310px;
  min-height: 255px;
  padding: 1.25rem 1.25rem 1.35rem;
  border-top: 5px solid rgba(76, 175, 80, 0.9);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgb(0 0 0 / 17%);
  cursor: default;
  transition: border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card:hover {
  transform: translateY(-2px);
}

.tool-card-green:hover {
  border-color: rgba(31, 121, 44, 0.92);
  box-shadow: 0 2px 10px rgba(26, 88, 39, 0.52);
}

.tool-card-blue:hover {
  border-color: rgba(29, 113, 146, 0.93);
  box-shadow: 0 2px 10px rgba(26, 57, 88, 0.52);
}

.tool-card-violet:hover {
  border-color: #d58a21;
  box-shadow: 0 2px 12px rgba(182, 117, 24, 0.42);
}

.tool-card-title {
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.7rem;
  margin: 0 0 1rem;
}

.tool-icon {
  flex: 0 0 auto;
  width: auto;
  height: auto;
  margin: 0;
  font-size: 1.8rem;
  line-height: 1;
}

.tool-icon-green {
  color: #1f8a55;
}

.tool-icon-blue {
  color: #2d6f91;
}

.tool-icon-violet {
  color: #7a5a8f;
}

.tool-card h3 {
  margin: 0;
  color: #2e7d32;
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: 0;
  white-space: nowrap;
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card:hover h3 {
  color: rgb(22, 93, 33);
  transform: scale(1.05);
}

.tool-card-blue:hover h3 {
  color: rgb(32, 86, 107);
}

.tool-card-violet:hover h3 {
  color: #5a4010;
}

.tool-card-violet:hover .tool-icon-violet {
  color: #b67518;
}

.tool-card p {
  max-width: none;
  margin: 0 auto;
  border-top: 2px solid #b2b2b2;
  padding: 1rem 0.3rem 0.35rem;
  color: #101f1d;
  font-size: 1rem;
  line-height: 1.58;
}

.coming-soon {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 2rem auto 0;
  color: var(--ink);
  font-size: 1.2rem;
  font-weight: 900;
  text-align: center;
}

.rocket-icon {
  display: block;
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.resources-image-section {
  width: 100%;
  aspect-ratio: 3 / 1;
  margin: clamp(3.2rem, 5vw, 5rem) 0 0;
  overflow: hidden;
  background: #ffffff;
}

.resources-composite-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.resources-grid {
  display: grid;
  grid-template-columns: minmax(350px, 0.68fr) minmax(0, 1fr);
  align-items: stretch;
  gap: clamp(0.8rem, 1.25vw, 1.35rem);
  width: 100%;
  margin: clamp(3.2rem, 5vw, 5rem) 0 0;
  padding: 0;
  background: transparent;
  box-sizing: border-box;
  overflow: hidden;
}

.resources-eyebrow {
  display: none;
  grid-column: 1 / -1;
  justify-self: center;
  width: min(100%, 1500px);
  margin: 0 0 1rem;
  text-align: left;
}

.resource-panel {
  box-sizing: border-box;
  min-width: 0;
  min-height: 340px;
  padding: clamp(1.45rem, 2.6vw, 2.35rem) clamp(1.45rem, 2.8vw, 2.6rem);
  background: linear-gradient(135deg, #eff8f3 0%, #f9fdfb 58%, #eef8f3 100%);
  border: 1px solid rgba(18, 63, 54, 0.08);
  border-radius: 0;
  box-shadow: 0 14px 32px rgba(18, 63, 54, 0.05);
}

.ai-panel {
  border-left: 0;
}

.flow-panel {
  border-right: 0;
}

.ai-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  overflow: hidden;
}

.flow-panel {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  text-align: center;
  overflow: hidden;
}

.ai-panel .eyebrow,
.flow-panel .eyebrow {
  margin-bottom: clamp(0.75rem, 1vw, 1rem);
  color: var(--section-green);
  font-size: clamp(0.8rem, 1vw, 0.92rem);
}

.ai-panel p:not(.eyebrow),
.flow-panel p:not(.eyebrow) {
  margin: 0;
  color: #163b38;
  font-size: clamp(0.98rem, 1.25vw, 1.16rem);
  line-height: 1.5;
}

.ai-panel-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(120px, 12vw, 180px);
  align-items: start;
  gap: clamp(1rem, 2vw, 1.8rem);
}

.ai-resource-icon {
  display: block;
  justify-self: end;
  width: clamp(112px, 12vw, 172px);
  height: clamp(112px, 12vw, 172px);
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(13, 107, 76, 0.08));
}

.speed-row {
  display: grid;
  grid-template-columns: minmax(0, auto) clamp(42px, 5vw, 72px) minmax(0, auto);
  align-items: end;
  justify-content: center;
  gap: clamp(0.65rem, 1.35vw, 1.45rem);
  margin-top: 0.45rem;
  color: var(--ink);
}

.speed-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.1rem;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.speed-row strong {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  color: var(--ink);
  font-size: clamp(2.85rem, 4.7vw, 4.4rem);
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.speed-row .speed-pages {
  max-width: none;
  align-items: baseline;
  color: var(--ink);
  font-size: clamp(2.85rem, 4.7vw, 4.4rem);
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.speed-row strong span {
  font-size: 0.52em;
}

.speed-row .speed-pages span {
  font-size: 0.34em;
}

.speed-after {
  padding-top: 1.8rem;
}

.speed-note {
  margin: clamp(0.9rem, 1.5vw, 1.2rem) 0 0;
  color: #163b38;
  font-size: clamp(0.98rem, 1.25vw, 1.15rem);
  line-height: 1.35;
  text-align: center;
}

.flow-arrow,
.speed-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--green);
}

.flow-arrow svg {
  width: 42px;
  height: 24px;
  overflow: visible;
}

.speed-arrow svg {
  width: 56px;
  height: 24px;
  overflow: visible;
}

.flow-arrow path,
.speed-arrow path {
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.flow-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 0.24fr);
  align-items: center;
  gap: clamp(1rem, 1.8vw, 1.7rem);
  width: 100%;
  min-height: 250px;
}

.flow-main {
  min-width: 0;
}

.software-flow {
  display: grid;
  grid-template-columns: minmax(105px, 1fr) auto minmax(105px, 1fr) auto minmax(
      105px,
      1fr
    );
  align-items: center;
  gap: clamp(0.55rem, 1vw, 1rem);
  width: 100%;
  min-width: 0;
  margin: clamp(1rem, 1.5vw, 1.35rem) 0 clamp(1rem, 1.5vw, 1.35rem);
}

.flow-node {
  min-width: 0;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 0.6rem;
  border: 1px solid rgba(13, 107, 76, 0.12);
  border-radius: 8px;
  background: #fff;
  color: var(--ink);
  box-shadow: 0 10px 24px rgba(19, 63, 54, 0.07);
  text-align: center;
}

.flow-node-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
  filter: drop-shadow(0 4px 9px rgba(13, 107, 76, 0.1));
}

.flow-node-logo {
  width: 58px;
  height: 58px;
  object-fit: contain;
}

.flow-node strong {
  font-size: clamp(0.78rem, 0.92vw, 0.94rem);
  line-height: 1.2;
}

.compatibility-strip {
  display: block;
  align-self: stretch;
  width: auto;
  margin: 0;
  border-top: 0;
  border-left: 1px solid rgba(18, 63, 54, 0.14);
  padding: 0.9rem 0 0.9rem clamp(1.25rem, 2vw, 1.8rem);
  color: var(--ink);
  text-align: left;
}

.compatibility-strip span {
  display: block;
  margin: 0 0 0.7rem;
  color: #4c6058;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.compatibility-strip ul {
  display: grid;
  gap: 0.46rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.compatibility-strip li {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--ink);
  font-size: clamp(0.95rem, 1.18vw, 1.1rem);
  font-weight: 800;
}

.compatibility-strip li::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
}

.compatibility-strip small {
  display: block;
  margin-top: 1.7rem;
  color: var(--muted);
  font-size: clamp(0.88rem, 1vw, 1rem);
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 1.35rem;
  margin-top: 2rem;
  text-align: left;
}

.team-section h2 {
  font-size: clamp(1.8rem, 2.55vw, 2.35rem);
  line-height: 1.08;
}

.team-section {
  padding-bottom: clamp(1.2rem, 2.4vw, 2rem);
}

.team-card {
  min-height: 126px;
  display: flex;
  flex-direction: column;
  padding: 0.95rem 1.15rem;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.team-card:hover,
.team-card-open {
  box-shadow: var(--shadow);
}

.team-card-open {
  align-self: stretch;
  height: 100%;
}

.team-summary {
  width: 100%;
  display: grid;
  grid-template-columns: 94px minmax(0, 1fr);
  align-items: start;
  gap: 1rem;
  min-height: 92px;
  text-align: left;
}

.team-card:hover h3,
.team-card:focus-visible h3 {
  color: var(--green);
}

.team-card:focus-visible {
  outline: 2px solid rgba(13, 107, 76, 0.42);
  outline-offset: 5px;
}

.team-summary-text {
  min-height: 92px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.team-card img {
  width: 86px;
  height: 86px;
  margin-top: 0.1rem;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 8px 18px rgba(18, 63, 54, 0.16);
}

.team-card h3 {
  min-height: 1.35rem;
}

.team-card .team-summary-text > p {
  min-height: 1.45rem;
}

.team-more {
  display: inline-flex;
  margin-top: 0.55rem;
  color: var(--green);
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.team-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  border-top: 1px solid var(--border);
  padding-top: 1rem;
}

.team-detail p {
  margin-bottom: 0.6rem;
  color: var(--muted);
  font-size: 0.86rem;
  line-height: 1.55;
}

.team-detail blockquote {
  margin: auto 0 0;
  border-left: 3px solid var(--green);
  padding: 0.25rem 0 0.25rem 0.9rem;
  color: var(--ink);
  font-size: 0.87rem;
  font-style: italic;
  font-weight: 800;
  line-height: 1.5;
}

.team-detail-enter-active,
.team-detail-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.team-detail-enter,
.team-detail-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.final-cta {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(240px, 280px);
  align-items: center;
  justify-content: center;
  gap: clamp(1.2rem, 3.2vw, 3rem);
  width: min(100% - 4rem, 1460px);
  margin: clamp(1.4rem, 3vw, 2.6rem) auto clamp(2rem, 4vw, 3.2rem);
  padding: clamp(1.35rem, 2.4vw, 2rem) clamp(1.6rem, 3vw, 2.7rem);
  border-radius: 18px;
  background: linear-gradient(90deg, #e8f3ed 0%, #f7fbf8 100%);
  border: 1px solid var(--border);
  box-shadow: 0 18px 42px rgba(18, 63, 54, 0.08);
}

.cta-copy {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  justify-self: start;
  gap: clamp(0.9rem, 1.4vw, 1.25rem);
  min-width: 0;
}

.final-cta h2 {
  font-size: clamp(1.45rem, 2.15vw, 2rem);
  line-height: 1.12;
  margin: 0 0 0.42rem;
}

.final-cta p {
  margin: 0;
}

.cta-copy p {
  color: var(--muted);
  font-size: clamp(0.98rem, 1.35vw, 1.25rem);
  line-height: 1.35;
}

.cta-icon-shell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(78px, 6vw, 104px);
  height: clamp(78px, 6vw, 104px);
  border-radius: 50%;
  background: rgba(214, 237, 224, 0.72);
}

.cta-icon {
  display: block;
  width: 62%;
  height: 62%;
  object-fit: contain;
}

.cta-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 0;
  justify-self: start;
  text-align: center;
}

.cta-actions .primary-action {
  width: 100%;
  min-width: 0;
  justify-content: center;
  padding: 0.86rem 1rem;
  font-size: clamp(0.68rem, 0.8vw, 0.78rem);
  line-height: 1.2;
  white-space: normal;
  text-align: center;
  box-shadow: 0 14px 28px rgba(13, 107, 76, 0.22);
}

.site-footer {
  background: #fbfdfc;
  color: var(--muted);
  border-top: 1px solid rgba(18, 63, 54, 0.12);
  padding: clamp(1.7rem, 3vw, 2.35rem) 1rem clamp(1.1rem, 2vw, 1.5rem);
}

.footer-inner {
  width: min(100% - 4rem, 1460px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr repeat(3, minmax(150px, 1fr));
  align-items: start;
  column-gap: clamp(1.4rem, 3.2vw, 3.8rem);
  row-gap: 1.25rem;
  font-size: 0.9rem;
}

.footer-brand,
.footer-links,
.footer-contact,
.footer-social {
  display: flex;
  flex-direction: column;
  gap: 0.38rem;
}

.footer-brand {
  align-items: flex-start;
  color: var(--ink);
}

.footer-brand-row {
  display: flex;
  align-items: center;
  gap: 0.62rem;
  font-size: 1.28rem;
  font-weight: 700;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.footer-brand img {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.footer-brand p {
  margin: 0.35rem 0 0;
  color: #6c7974;
  font-size: clamp(0.78rem, 1vw, 0.92rem);
}

.footer-links {
  align-items: flex-start;
  border-left: 1px solid rgba(18, 63, 54, 0.12);
  padding-left: clamp(1.5rem, 3vw, 3rem);
}

.footer-contact {
  align-items: flex-start;
  border-left: 1px solid rgba(18, 63, 54, 0.12);
  padding-left: clamp(1.5rem, 3vw, 3rem);
}

.footer-links span,
.footer-contact span,
.footer-social span:first-child {
  color: var(--green);
  font-size: clamp(0.88rem, 1.08vw, 1.04rem);
  font-weight: 900;
}

.footer-social {
  align-items: flex-start;
  border-left: 1px solid rgba(18, 63, 54, 0.12);
  padding-left: clamp(1.5rem, 3vw, 3rem);
}

.footer-links a,
.footer-contact a {
  color: var(--muted);
  font-size: clamp(0.82rem, 1vw, 0.95rem);
  font-weight: 500;
  text-decoration: none;
}

.footer-links a:hover,
.footer-contact a:hover {
  text-decoration: underline;
}

.social-icons {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--green);
}

.social-icons a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 30px;
  color: var(--green);
  font-size: 1.65rem;
  font-weight: 900;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

.social-icons a:hover,
.social-icons a:focus-visible {
  color: var(--green-dark);
  transform: translateY(-1px);
}

.social-icons svg {
  width: 31px;
  height: 31px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.footer-company {
  grid-column: 1 / -1;
  margin: 0;
  border-top: 1px solid rgba(18, 63, 54, 0.12);
  padding-top: 0.85rem;
  color: #52675f;
  font-size: 0.74rem;
  line-height: 1.45;
  text-align: center;
}

@media (max-width: 1400px) {
  .hero-section {
    grid-template-columns: minmax(0, 36%) minmax(0, 64%);
  }
}

@media (max-width: 1080px) {
  .welcome-page section,
  .site-footer {
    scroll-margin-top: 138px;
  }

  .hero-section {
    grid-template-columns: 1fr;
  }

  .hero-visual {
    grid-column: 1;
    grid-row: 1;
    width: 100%;
  }

  .hero-inner {
    grid-column: 1;
    grid-row: 2;
    padding: 0;
  }

  .hero-content {
    width: min(100%, 580px);
    padding: 2rem clamp(2rem, 8vw, 5rem) 3.2rem;
  }

  .site-header {
    grid-template-columns: auto auto;
  }

  .main-nav {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 0.2rem;
  }

  .main-nav button {
    font-size: 0.84rem;
    padding-inline: 0.85rem;
  }

  .about-section,
  .product-section,
  .resources-grid {
    grid-template-columns: 1fr;
  }

  .resources-grid {
    gap: 1rem;
  }

  .resource-panel {
    min-height: auto;
    border-right: 0;
    border-left: 0;
  }

  .flow-panel {
    padding: 1.35rem;
  }

  .product-section {
    gap: 1.6rem;
  }

  .product-copy p:not(.eyebrow):not(.product-subtitle) {
    max-width: 560px;
  }

  .product-checklist {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ai-panel-top {
    grid-template-columns: minmax(0, 1fr) 150px;
  }

  .pillars-grid,
  .advantages-grid,
  .tools-grid,
  .team-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .vision-band {
    grid-template-columns: 210px 1fr;
  }

  .vision-icon {
    width: 205px;
    height: 205px;
  }

  .vision-copy {
    grid-column: 2;
  }

  .final-cta {
    grid-template-columns: minmax(0, 0.82fr) minmax(220px, 260px);
  }

  .final-cta .cta-actions {
    grid-column: 2;
    justify-self: start;
  }

  .footer-inner {
    grid-template-columns: 1fr 1fr;
  }

  .footer-social {
    grid-column: 2;
  }
}

@media (max-width: 740px) {
  .welcome-page section,
  .site-footer {
    scroll-margin-top: 18px;
  }

  .site-header {
    position: relative;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.75rem;
  }

  .brand-link {
    min-width: 0;
    justify-self: start;
  }

  .client-area-link {
    justify-self: end;
    padding: 0.45rem 0.65rem;
    white-space: nowrap;
  }

  .hero-section {
    align-items: flex-start;
  }

  .hero-visual {
    width: 100%;
  }

  .hero-content {
    width: auto;
    margin: 0;
    padding: 1.5rem 1rem 3rem;
  }

  .hero-inner {
    padding: 0;
  }

  .hero-actions,
  .final-cta {
    align-items: stretch;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }

  .pillars-grid,
  .advantages-grid,
  .tools-grid,
  .team-grid,
  .footer-inner,
  .final-cta {
    grid-template-columns: 1fr;
  }

  .product-checklist {
    grid-template-columns: 1fr;
  }

  .tools-grid {
    width: 100%;
    padding: 1rem 0;
  }

  .resources-image-section {
    aspect-ratio: auto;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .resources-composite-image {
    width: 64rem;
    max-width: none;
    height: calc(64rem / 3);
  }

  .tools-section .tools-title,
  .team-section h2 {
    font-size: clamp(1.75rem, 9vw, 2.3rem);
  }

  .tool-card {
    max-width: min(100%, 20rem);
  }

  .coming-soon {
    gap: 0.55rem;
    font-size: 1rem;
  }

  .rocket-icon {
    width: 40px;
    height: 40px;
  }

  .tool-card h3 {
    white-space: normal;
  }

  .pillar,
  .advantage {
    border-left: 0;
    border-top: 1px solid var(--border);
  }

  .pillar:first-child,
  .advantage:first-child {
    border-top: 0;
  }

  .vision-band {
    grid-template-columns: 1fr;
    justify-items: stretch;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: left;
  }

  .vision-copy,
  .final-cta .cta-actions {
    grid-column: auto;
    grid-row: auto;
  }

  .vision-copy {
    text-align: left;
  }

  .vision-icon {
    justify-self: center;
    width: 190px;
    height: 190px;
  }

  .final-cta {
    gap: 1rem;
    margin-top: 2rem;
    padding: 1.35rem;
    text-align: center;
  }

  .cta-copy {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.65rem;
  }

  .cta-actions {
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    min-width: 0;
  }

  .cta-actions .primary-action {
    width: 100%;
  }

  .software-flow {
    grid-template-columns: 1fr;
    justify-items: center;
    width: min(100%, 18rem);
  }

  .resources-grid {
    width: 100%;
    margin-top: 2rem;
    padding-right: 0;
    padding-left: 0;
    padding-bottom: 1.4rem;
  }

  .resource-panel {
    padding: 1.35rem 1rem;
  }

  .ai-panel-top {
    grid-template-columns: 1fr;
    justify-items: start;
  }

  .ai-resource-icon {
    justify-self: center;
    width: 132px;
    height: 132px;
  }

  .speed-row {
    grid-template-columns: 1fr 42px 1fr;
    margin-top: 0.55rem;
    gap: 0.5rem;
  }

  .speed-row strong {
    font-size: clamp(2.2rem, 12vw, 3.3rem);
  }

  .speed-arrow svg {
    width: 42px;
  }

  .flow-content {
    grid-template-columns: 1fr;
    min-height: auto;
    justify-items: center;
  }

  .flow-main {
    width: 100%;
  }

  .compatibility-strip {
    width: 100%;
    margin-top: 0.45rem;
    border-top: 1px solid rgba(18, 63, 54, 0.12);
    border-left: 0;
    padding: 0.85rem 0 0;
    text-align: center;
  }

  .compatibility-strip ul {
    justify-items: center;
  }

  .flow-node {
    width: min(100%, 18rem);
    min-height: 104px;
  }

  .flow-arrow {
    transform: rotate(90deg);
    justify-self: center;
  }

  .team-summary {
    grid-template-columns: 78px 1fr;
  }

  .team-card img {
    width: 70px;
    height: 70px;
  }

  .footer-brand,
  .footer-links,
  .footer-contact,
  .footer-social {
    align-items: center;
    text-align: center;
    border-left: 0;
    padding-left: 0;
  }

  .footer-brand-row {
    justify-content: center;
  }

  .footer-company {
    text-align: center;
  }
}

@media (max-width: 440px) {
  .brand-logo {
    width: 52px;
    height: 52px;
  }

  .brand-link {
    font-size: 1.08rem;
  }

  .client-area-link {
    padding-right: 0.5rem;
    padding-left: 0.5rem;
    font-size: 0.68rem;
  }

  .main-nav button {
    font-size: 0.8rem;
    padding-inline: 0.7rem;
  }

  .hero-content h1 {
    font-size: 2.05rem;
  }

  .speed-row {
    grid-template-columns: 1fr;
    justify-items: stretch;
  }

  .speed-arrow {
    transform: rotate(90deg);
  }
}
</style>
